/**
 * Slim selectable header row for a rule-based path group.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { VoluumPathGroupUpdate } from '@trafficpulse/shared';

import { Switch } from '@/components/ui/form/controls/Switch';
import { cn } from '@/lib/utils';

import PathGroupHeaderActions from './PathGroupHeaderActions';

export interface PathGroupHeaderProps {
  group: VoluumPathGroupUpdate;
  groupName: string;
  isHeaderSelected: boolean;
  isExpanded: boolean;
  /** Chevron control — independent of selecting the group (right pane). */
  onToggleExpanded: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onSelectHeader: () => void;
  onPatch: (mutator: (g: VoluumPathGroupUpdate) => void) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const PathGroupHeader: FC<PathGroupHeaderProps> = (props) => {
  const {
    group,
    groupName,
    isExpanded,
    isHeaderSelected,
    onToggleExpanded,
    onSelectHeader,
    onPatch,
  } = props;

  return (
    <div className="flex items-center gap-1.5 px-2 py-1">
      <button
        type="button"
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center self-center rounded-md border border-border/70 bg-muted/40 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpanded();
        }}
        aria-expanded={isExpanded}
        aria-label={
          isExpanded ? `Collapse ${groupName}` : `Expand ${groupName}`
        }
      >
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        )}
      </button>
      <div
        role="button"
        tabIndex={0}
        className={cn(
          'min-w-0 flex-1 cursor-pointer rounded-sm px-0.5 py-0.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring',
          group.active ? 'text-success' : 'text-muted-foreground',
        )}
        aria-pressed={isHeaderSelected}
        onClick={onSelectHeader}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectHeader();
          }
        }}
      >
        <span className="block truncate text-xs font-semibold leading-tight">
          {groupName}
        </span>
      </div>
      <PathGroupHeaderActions
        groupName={groupName}
        canMoveUp={props.canMoveUp}
        canMoveDown={props.canMoveDown}
        onMoveUp={props.onMoveUp}
        onMoveDown={props.onMoveDown}
        onDuplicate={props.onDuplicate}
        onDelete={props.onDelete}
      />
      <div onClick={(e) => e.stopPropagation()}>
        <Switch
          checked={group.active}
          onCheckedChange={(v) =>
            onPatch((g) => {
              g.active = v;
            })
          }
          aria-label={`Toggle group ${groupName}`}
        />
      </div>
    </div>
  );
};

export default PathGroupHeader;
